/**
 * Saved Colours — custom backend API client.
 *
 * Talks to a merchant-provided REST endpoint (e.g. a small Laravel app)
 * that reads/writes the saved-colours JSON on the customer's
 * `custom.saved_colours` metafield via Shopify's Admin API, server-side.
 *
 * This replaces an earlier implementation that called Shopify's Customer
 * Account API directly from the browser via OAuth (Authorization Code +
 * PKCE). That approach hit a hard CORS wall: a classic (non-headless)
 * theme calling back into its own storefront domain was rejected by
 * Shopify's Customer Account API GraphQL endpoint even after registering
 * the storefront as an authorized JavaScript origin — that API appears to
 * assume a genuinely separate headless frontend domain, not a Dawn theme
 * calling itself. Talking to a merchant-controlled endpoint instead
 * sidesteps that entirely (it can set its own CORS headers, or none are
 * needed at all if it's same-origin via a proxy).
 *
 * GET and POST are configured as two separate endpoint URLs (theme
 * settings "Saved Colours API endpoint URL (GET)" / "(POST)"), since a
 * backend may route reads and writes to different paths.
 *
 * Endpoint contract this client expects (confirmed against the live
 * backend's actual responses — see extractStateValue() for every shape
 * it's read tolerantly):
 *   GET  {getEndpointUrl}?customer_id={id}
 *     -> 200 { success, data: { customer_id, key, value: <state object> } }
 *   POST {postEndpointUrl}
 *     body: { "customer_id": "{id}", "metafields": "<JSON string>" }
 *     -> any 2xx on success
 *
 * fetchRemoteState() resolves { success, value } rather than a bare value:
 * `success: false` means "could not reliably read the remote state" (network
 * error, bad response, unrecognised shape) — callers MUST treat that as
 * "don't know", never as "customer has nothing saved", or a parsing hiccup
 * silently overwrites real server data with an empty merge. This is not
 * theoretical: it's exactly what happened before this shape was fixed —
 * GET's response wasn't recognised, was treated as empty, merged with an
 * empty local state, and pushed back, wiping a customer's real 12 saved
 * colours down to zero. `success: true, value: null` means the fetch genuinely
 * confirmed the customer has no saved data yet — that IS safe to treat
 * as empty.
 *
 * Both requests send an `Authorization: Bearer {apiKey}` header if an API
 * key is configured. That key is necessarily visible in the browser (it
 * ships in this theme JS) — it identifies "this is the theme calling",
 * it is NOT a secret that protects customer data on its own. The backend
 * should treat `customer_id` as unverified input from the browser rather
 * than trusting it blindly.
 */
(function (global) {
  'use strict';

  function readConfig() {
    return (global.savedColoursConfig && global.savedColoursConfig.customerApi) || null;
  }

  // Read and write are gated independently — a merchant can wire up just
  // one endpoint first (e.g. POST to start testing writes) without the
  // other being required. isConfigured() is the broad "is sync turned on
  // at all" check (true if enabled and at least one endpoint is set).
  function canRead() {
    var config = readConfig();
    return !!(config && config.enabled && config.getEndpointUrl);
  }

  function canWrite() {
    var config = readConfig();
    return !!(config && config.enabled && config.postEndpointUrl);
  }

  function isConfigured() {
    return canRead() || canWrite();
  }

  function getCustomerId() {
    return (global.customerConfig && global.customerConfig.customerId) || null;
  }

  function buildHeaders(config) {
    var headers = { 'Content-Type': 'application/json' };
    if (config.apiKey) headers.Authorization = 'Bearer ' + config.apiKey;
    return headers;
  }

  /**
   * Fetches the customer's saved-colours JSON string from the backend.
   * ALWAYS resolves an object — { success, value } — never a bare
   * string/null, and never rejects. `success: false` means "could not
   * reliably read the remote state" (missing config, no customer id,
   * network error, unrecognised response shape) — callers MUST treat
   * that as "don't know", never as "customer has nothing saved", or a
   * parsing hiccup silently overwrites real server data with an empty
   * merge (this happened for real before the response shape below was
   * fixed). `success: true, value: null` means the fetch genuinely
   * confirmed the customer has no saved data yet — that IS safe to treat
   * as empty.
   */
  function fetchRemoteState() {
    var config = readConfig();
    var customerId = getCustomerId();
    if (!canRead() || !customerId) return Promise.resolve({ success: false, value: null });

    var separator = config.getEndpointUrl.indexOf('?') > -1 ? '&' : '?';
    var url = config.getEndpointUrl + separator + 'customer_id=' + encodeURIComponent(customerId)+'&key=saved_colours';
    // var url = config.getEndpointUrl;
    console.log("url::",url);
    return fetch(url, {
      method: 'GET',
      headers: buildHeaders(config)
      // body: JSON.stringify({ customer_id: customerId, key: 'saved_colours' }),
    })
    .then(function (response) {
      // This backend signals "customer has nothing saved yet" as a
      // `success: false` JSON body (see extractStateValue()'s "not found"
      // check below), not as a distinct HTTP status — so the body has to
      // be read and inspected even when the response isn't a plain 200,
      // rather than treating non-ok as an automatic hard failure.
      return response.json().catch(function () { return null; });
    })
    .then(function (payload) {
      return extractStateValue(payload);
    })
    .catch(function () {
      return { success: false, value: null };
    });
  }

  /**
   * Reads the saved-colours state out of a GET response, tolerantly —
   * the backend's exact wrapping has drifted more than once. Checked in
   * order:
   *   1. { data: { value: <state object> } }               — the confirmed live shape
   *   2. { data: { metafields: [{ value: <state> }, ...] } } — the shape POST's own response uses
   *   3. { value: <state> } / { metafields: <state> }        — earlier, unwrapped guesses
   *   4. the payload itself, if it already looks like a state object
   * Returns { success: false, value: null } if the response was received
   * but matched none of these — the caller must treat that as "unknown",
   * not "empty" (see fetchRemoteState doc comment above).
   */
  function extractStateValue(payload) {
    if (payload == null) return { success: false, value: null };

    // The backend reports "this customer has never saved anything" as
    // { success: false, message: "Metafield 'saved_colours' value not
    // found." } — confirmed against the live backend. That is NOT an
    // error: it's a definitive, safe-to-treat-as-empty answer, so it
    // must resolve success: true (with a null value) rather than falling
    // into the "unknown, don't touch" bucket below — otherwise the local
    // side never gets merged/pushed for a brand-new remote account. Any
    // other success:false response (auth failure, server error, some
    // other message) still falls through to the unknown/unsafe path.
    if (
      payload.success === false &&
      typeof payload.message === 'string' &&
      /not found/i.test(payload.message)
    ) {
      return { success: true, value: null };
    }

    var data = payload.data;
    var raw = null;

    if (data && data.value != null) {
      raw = data.value;
    } else if (data && Array.isArray(data.metafields) && data.metafields[0] && data.metafields[0].value != null) {
      raw = data.metafields[0].value;
    } else if (payload.value != null) {
      raw = payload.value;
    } else if (payload.metafields != null) {
      raw = payload.metafields;
    } else if (payload.colours || payload.groups) {
      raw = payload;
    }

    if (raw == null) {
      // The request succeeded but none of the response shapes we know
      // how to read matched — surface this loudly rather than quietly
      // treating a real backend response as "no saved colours".
      console.warn('Saved Colours: GET response did not contain a recognisable value/metafields field.', payload);
      return { success: false, value: null };
    }

    return { success: true, value: typeof raw === 'string' ? raw : JSON.stringify(raw) };
  }

  /**
   * Writes the given (already-stringified) state JSON to the backend.
   * Resolves true/false, never rejects.
   *
   * options.keepalive: pass true when writing during page unload/hide
   * (e.g. the user navigates away right after saving something) — this
   * tells the browser to let the request complete even after the page
   * is gone, instead of it being silently cancelled mid-flight.
   */
  function writeRemoteState(stateJsonString, options) {
    console.log("stateJsonString::",stateJsonString);
    var config = readConfig();
    var customerId = getCustomerId();
    if (!canWrite() || !customerId) return Promise.resolve(false);

    var fetchOptions = {
      method: 'POST',
      headers: buildHeaders(config),
      body: JSON.stringify({ customer_id: customerId, metafields: stateJsonString }).replaceAll("\\",'').replace('"{','{').replace('}"','}'),
    };
    if (options && options.keepalive) fetchOptions.keepalive = true;

    return fetch(config.postEndpointUrl, fetchOptions)
    .then(function (response) {
      return response.ok;
    })
    .catch(function () {
      return false;
    });
  }

  global.SavedColoursCustomerAPI = {
    isConfigured: isConfigured,
    canRead: canRead,
    canWrite: canWrite,
    getCustomerId: getCustomerId,
    fetchRemoteState: fetchRemoteState,
    writeRemoteState: writeRemoteState,
  };
})(window);
